import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { ItemListItemKind, ItemListRowData, ItemListRowType } from "@/components/item-list/types";
import { EventContext } from "@/domains/metrics/context";
import { liveQuery, Subscription } from "dexie";
import { DateTime } from "luxon";
import { groupLens, LensGroup } from "@/modules/timeline";
import { startCase } from "lodash-es";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { SyncModelKind } from "@/store/sync/types";
import { SessionModelData } from "@/store/sessions/types";
import { itemsModule } from "@/modules/items";

const DEFAULT_LIMIT = 50;

type SessionListEntry = {
  id: string;
  title?: string | null;
  status?: string | null;
  kind?: string | null;
  createdAt?: string | null;
  lastUserActionAt?: string | null;
};

export class SessionsListPageStore extends AppSubStore {
  supportsAddToCollectionListAction = false;
  supportsMoveNoteToTrashListAction = false;
  supportsMoveToTrashListAction = false;
  listState: ListStateObservable;

  isLoading = true;
  subscriptionInitialized = false;
  liveQuerySubscription?: Subscription;
  sessions: SessionListEntry[] = [];

  eventContext = EventContext.Unknown;

  private limit = DEFAULT_LIMIT;
  private currentScrollTop = 0;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.listState = new ListStateObservable({
      ...injectedDeps,
      listStateProvider: this,
      canSelect: false,
    });

    makeObservable<
      this,
      | "runLiveQuery"
      | "limit"
      | "currentScrollTop"
      | "toListEntry"
      | "mapSessionToRow"
      | "getKindLabel"
      | "buildExtraRow"
      | "renderStatus"
      | "resolveDate"
    >(this, {
      toListEntry: false,
      mapSessionToRow: false,
      getKindLabel: false,
      buildExtraRow: false,
      renderStatus: false,
      resolveDate: false,
      supportsAddToCollectionListAction: true,
      supportsMoveNoteToTrashListAction: true,
      supportsMoveToTrashListAction: true,
      listState: true,
      eventContext: true,
      initialize: true,
      useEffects: true,
      isLoading: observable,
      subscriptionInitialized: observable,
      liveQuerySubscription: observable,
      sessions: observable,
      limit: observable,
      currentScrollTop: observable,
      groupedSessions: computed,
      limitedSessions: computed,
      itemRows: computed,
      orderedItemIds: computed,
      hasNoItems: computed,
      canLoadMore: computed,
      scrollTop: computed,
      runLiveQuery: action,
      increaseLimit: action,
      setScrollTop: action,
      resetState: action,
    });

    reaction(
      () => this.subscriptionInitialized,
      initialized => {
        if (initialized) {
          setTimeout(() => runInAction(() => (this.isLoading = false)), 100);
        }
      }
    );
  }

  get groupedSessions(): LensGroup<SessionListEntry>[] {
    return groupLens(
      this.limitedSessions.map(session => ({
        dateTime:
          this.resolveDate(session.lastUserActionAt || session.createdAt)?.toJSDate() ?? new Date(),
        item: session,
      }))
    );
  }

  get limitedSessions(): SessionListEntry[] {
    return this.sessions.slice(0, this.limit);
  }

  get itemRows(): ItemListRowData[] {
    const rows: ItemListRowData[] = [];

    for (const group of this.groupedSessions) {
      rows.push({
        type: ItemListRowType.SectionHeader,
        key: group.title,
        payload: { title: group.title },
      });

      for (const entry of group.items) {
        rows.push(this.mapSessionToRow(entry.item));
      }
    }

    if (rows.length) {
      rows.push({
        type: ItemListRowType.Padding,
        key: "padding",
        payload: { height: 50 },
      });
    }

    return rows;
  }

  get orderedItemIds(): string[] {
    return this.limitedSessions.map(session => session.id);
  }

  get hasNoItems(): boolean {
    return !this.isLoading && this.sessions.length === 0;
  }

  get canLoadMore(): boolean {
    return this.sessions.length > this.limit;
  }

  get scrollTop(): number {
    return this.currentScrollTop;
  }

  initialize() {
    this.limit = DEFAULT_LIMIT;
  }

  useEffects() {
    useEffectOnMount(() => {
      const subscription = this.runLiveQuery();
      return () => subscription?.unsubscribe();
    });

    this.listState.useListStateEffects();
  }

  increaseLimit = () => {
    this.limit += DEFAULT_LIMIT;
  };

  setScrollTop = (value: number) => {
    this.currentScrollTop = value;
  };

  resetState = () => {
    this.limit = DEFAULT_LIMIT;
    this.setScrollTop(0);
    this.listState.clearSelection();
  };

  private runLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();

    this.liveQuerySubscription = liveQuery(() =>
      this.store.memDb.mappedTables[SyncModelKind.Session].local.toArray()
    ).subscribe({
      next: rows => {
        runInAction(() => {
          this.sessions = (rows as { model_data: SessionModelData; model_id: string }[])
            .map(row => this.toListEntry(row))
            .sort((a, b) => {
              const aDate = this.resolveDate(a.lastUserActionAt || a.createdAt)?.toMillis() || 0;
              const bDate = this.resolveDate(b.lastUserActionAt || b.createdAt)?.toMillis() || 0;
              return bDate - aDate;
            });
          this.subscriptionInitialized = true;
        });
      },
    });

    return this.liveQuerySubscription;
  }

  private toListEntry(row: { model_data: SessionModelData; model_id: string }): SessionListEntry {
    return {
      id: row.model_id,
      title: row.model_data.title,
      status: row.model_data.status,
      kind: row.model_data.kind,
      createdAt: row.model_data.created_at,
      lastUserActionAt: row.model_data.last_user_action_at,
    };
  }

  private mapSessionToRow(session: SessionListEntry): ItemListRowData {
    const lastActionLabel = this.resolveDate(
      session.lastUserActionAt || session.createdAt
    )?.toLocaleString(DateTime.DATETIME_MED);

    const title = session.title?.trim() || "Untitled session";

    const itemAppearance = itemsModule.resolveAppearance({
      itemId: session.id,
      appStore: this.store,
    });

    return {
      type: ItemListRowType.Item,
      key: session.id,
      payload: {
        id: session.id,
        kind: ItemListItemKind.Session,
        label: title,
        dateLabel: lastActionLabel,
        description: this.getKindLabel(session.kind),
        onClick: () => this.store.navigation.goToSession({ sessionId: session.id }),
        listState: this.listState,
        rightAlignedComponent: () => this.renderStatus(session.status),
        extraRow: this.buildExtraRow(session),
        appearance: itemAppearance,
      },
    };
  }

  private getKindLabel(kind?: string | null) {
    if (!kind) return undefined;
    return startCase(kind.toLowerCase());
  }

  private buildExtraRow(session: SessionListEntry) {
    const details: string[] = [];

    if (session.createdAt) {
      details.push(
        `Created ${this.resolveDate(session.createdAt)?.toLocaleString(DateTime.DATETIME_SHORT)}`
      );
    }

    if (session.lastUserActionAt) {
      details.push(
        `Last activity ${this.resolveDate(session.lastUserActionAt)?.toLocaleString(
          DateTime.DATETIME_SHORT
        )}`
      );
    }

    if (!details.length) return undefined;

    return {
      id: `${session.id}-details`,
      content: () => (
        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
          {details.join(" · ")}
        </MDSText>
      ),
    };
  }

  private renderStatus(status?: string | null) {
    if (!status) return null;

    return (
      <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
        {startCase(status.toLowerCase())}
      </MDSText>
    );
  }

  private resolveDate(value?: string | null) {
    if (!value) return undefined;
    const dt = DateTime.fromISO(value);
    return dt.isValid ? dt : undefined;
  }
}
